// The one rule for NEW passwords (sign-up, and any future change-password
// form), following "Form Validation, Usernames and Passwords Guide.md".
// Sign-in never checks it, so existing 6- and 7-character passwords keep
// working. This is a convenience check in the browser; the real limit is the
// Firebase Authentication password policy (Firebase console).

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

// Bundled list of very common passwords (compared in lower case), plus the
// app's own name. Shorter entries are already refused by the length rule.
const COMMON_PASSWORDS = new Set([
  "password",
  "password1",
  "password12",
  "password123",
  "password!",
  "passw0rd",
  "p@ssw0rd",
  "p@ssword",
  "12345678",
  "123456789",
  "1234567890",
  "0123456789",
  "12345678910",
  "123123123",
  "12341234",
  "123456123456",
  "87654321",
  "11111111",
  "00000000",
  "88888888",
  "11223344",
  "qwerty123",
  "qwerty12",
  "qwertyuiop",
  "qwerty1234",
  "1q2w3e4r",
  "1q2w3e4r5t",
  "1qaz2wsx",
  "zaq12wsx",
  "q1w2e3r4",
  "asdfghjkl",
  "asdf1234",
  "abc12345",
  "abcd1234",
  "abcdefgh",
  "a1b2c3d4",
  "iloveyou",
  "iloveyou1",
  "iloveyou2",
  "sunshine",
  "sunshine1",
  "princess",
  "princess1",
  "football",
  "football1",
  "baseball",
  "basketball",
  "superman",
  "batman123",
  "starwars",
  "whatever",
  "trustno1",
  "welcome1",
  "welcome123",
  "letmein1",
  "letmein123",
  "computer",
  "internet",
  "michelle",
  "jennifer",
  "jordan23",
  "liverpool",
  "chelsea1",
  "arsenal1",
  "mercedes",
  "corvette",
  "babygirl1",
  "blink182",
  "charlie1",
  "monkey123",
  "dragon123",
  "shadow123",
  "master123",
  "freedom1",
  "football123",
  "hello123",
  "hellohello",
  "changeme",
  "changeme1",
  "secret123",
  "admin123",
  "administrator",
  "loveyou1",
  "christmas",
  "november",
  "september",
  "december",
  "ebookplanner",
  "ebookplanner1",
  "ebookplanner123",
  "aiebookplanner",
  "aiebookplanner1",
  "aiebookplanner123",
  "ebook123",
  "ebooks123",
  "myebook123",
]);

// Any password containing the app's name is refused too.
const APP_NAME_PARTS = ["ebookplanner"];

export const PASSWORD_RULE_HINT = `Use at least ${PASSWORD_MIN_LENGTH} characters. Spaces and symbols are allowed.`;

// Returns null when the new password is acceptable, otherwise a message.
export function checkNewPassword(password, { email = "" } = {}) {
  const value = typeof password === "string" ? password : "";

  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Use at least ${PASSWORD_MIN_LENGTH} characters for your password.`;
  }
  if (value.length > PASSWORD_MAX_LENGTH) {
    return `Use at most ${PASSWORD_MAX_LENGTH} characters for your password.`;
  }

  const lower = value.toLowerCase();
  const compact = lower.replace(/[\s._-]+/g, "");

  if (/^(.)\1+$/su.test(value)) {
    return "Don't use one character repeated. Choose a longer mix.";
  }
  if (COMMON_PASSWORDS.has(lower) || COMMON_PASSWORDS.has(compact)) {
    return "That password is too common. Choose another one.";
  }
  if (APP_NAME_PARTS.some((part) => compact.includes(part))) {
    return "Don't use the app's name in your password.";
  }

  const emailName = String(email).split("@")[0].trim().toLowerCase();
  if (emailName.length >= 4 && lower.includes(emailName)) {
    return "Don't include your email name in your password.";
  }

  return null;
}
